import { http, HttpResponse } from "msw";
import {
  generateApproveCode,
  generateMockJwt,
  getState,
  updateState,
} from "../state";

// Service worker is shared across all clients; we need a module-local counter
// rather than sessionStorage to track auto-approve progress per callsign.
const enrollmentPollCounts = new Map<string, number>();

export const enrollmentHandlers = [
  http.get("/api/v1/enrollment/invitecode", ({ request }) => {
    const url = new URL(request.url);
    const code = url.searchParams.get("invitecode") || "";
    const state = getState();
    const found = state.inviteCodes.find(
      (c) => c.invitecode === code && c.active,
    );
    // Also accept any non-empty code so reviewers can paste arbitrary strings.
    return HttpResponse.json({
      invitecode_is_active: !!found || code.length > 2,
    });
  }),

  http.post("/api/v1/enrollment/invitecode/enroll", async ({ request }) => {
    const body = (await request.json()) as {
      callsign: string;
      invite_code: string;
    };
    const state = getState();
    const approveCode = generateApproveCode(body.callsign);
    const jwt = generateMockJwt(body.callsign);

    state.enrollments.push({
      callsign: body.callsign,
      approvecode: approveCode,
      state: 0, // PENDING
    });

    // Store token + callsign but NOT yet authenticated — the waiting-room
    // poll is what flips isAuthenticated once auto-approve fires.
    updateState({
      currentCallsign: body.callsign,
      currentRole: null,
      isAuthenticated: false,
      jwt,
      enrollments: state.enrollments,
    });

    localStorage.setItem("token", jwt);
    localStorage.setItem("callsign", body.callsign);
    localStorage.setItem("approveCode", approveCode);

    return HttpResponse.json({
      callsign: body.callsign,
      approvecode: approveCode,
      jwt,
    });
  }),

  http.get("/api/v1/enrollment/have-i-been-accepted", () => {
    const state = getState();
    const enrollment = state.enrollments.find(
      (e) => e.callsign === state.currentCallsign,
    );
    const accepted = enrollment?.state === 1;

    // Auto-approve after 3 polls (~15s at the default 5s interval) so the
    // waiting-room flow demos correctly without a human approver.
    if (enrollment && enrollment.state === 0 && state.currentCallsign) {
      const key = state.currentCallsign;
      const polls = (enrollmentPollCounts.get(key) ?? 0) + 1;
      enrollmentPollCounts.set(key, polls);

      if (polls >= 3) {
        enrollment.state = 1;
        enrollmentPollCounts.delete(key);
        if (!state.users.find((u) => u.callsign === enrollment.callsign)) {
          state.users.push({
            callsign: enrollment.callsign,
            roles: [],
            extre: null,
          });
        }
        updateState({
          isAuthenticated: true,
          currentRole: "user",
          users: state.users,
          enrollments: state.enrollments,
        });
        return HttpResponse.json({ have_i_been_accepted: true });
      }
      updateState({ enrollments: state.enrollments });
    }

    return HttpResponse.json({ have_i_been_accepted: accepted });
  }),

  http.post("/api/v1/enrollment/accept", async ({ request }) => {
    const body = (await request.json()) as {
      callsign: string;
      approvecode: string;
    };
    const state = getState();
    const enrollment = state.enrollments.find(
      (e) => e.callsign === body.callsign,
    );

    if (enrollment) {
      enrollment.state = 1;
      if (!state.users.find((u) => u.callsign === body.callsign)) {
        state.users.push({ callsign: body.callsign, roles: [], extre: null });
      }
      updateState({ enrollments: state.enrollments, users: state.users });
    }

    return HttpResponse.json({ success: true });
  }),

  http.post("/api/v1/enrollment/lock", async ({ request }) => {
    const body = (await request.json()) as {
      callsign: string;
      lock_reason: string;
    };
    const state = getState();
    const enrollment = state.enrollments.find(
      (e) => e.callsign === body.callsign,
    );
    if (enrollment) {
      enrollment.state = 2;
      updateState({ enrollments: state.enrollments });
    }
    return HttpResponse.json({ success: true });
  }),

  http.post("/api/v1/enrollment/promote", async ({ request }) => {
    const body = (await request.json()) as { callsign: string };
    const state = getState();
    const user = state.users.find((u) => u.callsign === body.callsign);
    if (user && !user.roles.includes("admin")) {
      user.roles.push("admin");
      updateState({ users: state.users });
    }
    return HttpResponse.json({ success: true });
  }),

  http.post("/api/v1/enrollment/demote", async ({ request }) => {
    const body = (await request.json()) as { callsign: string };
    const state = getState();
    const user = state.users.find((u) => u.callsign === body.callsign);
    if (user) {
      user.roles = user.roles.filter((r) => r !== "admin");
      updateState({ users: state.users });
    }
    return HttpResponse.json({ success: true });
  }),

  http.get("/api/v1/enrollment/list", () => {
    const state = getState();
    return HttpResponse.json({
      callsign_list: state.enrollments.map((e) => ({
        approvecode: e.approvecode,
        callsign: e.callsign,
        state: e.state,
      })),
    });
  }),

  http.get("/api/v1/enrollment/show-verification-code-info", ({ request }) => {
    const url = new URL(request.url);
    const code = url.searchParams.get("verification_code") || "";
    const state = getState();
    const enrollment = state.enrollments.find((e) => e.approvecode === code);
    if (enrollment) {
      return HttpResponse.json({
        state: String(enrollment.state),
        callsign: enrollment.callsign,
        accepted: enrollment.state === 1 ? "true" : "false",
        locked: enrollment.state === 2 ? "true" : "false",
      });
    }
    return HttpResponse.json({
      state: "0",
      callsign: "Unknown",
      accepted: "false",
      locked: "false",
    });
  }),
];
